import MuiLink from '@mui/material/Link';
import { useHasMenu } from 'auth/access';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import { updateEmploymentStatus } from '../../hr.api';
import { DataGrid, DropdownBtn } from '../../../shared/components';
import { capitalizeString, formatAmount } from '../../../shared/utils/misc';
import { formatEmploymentPeriod } from '../employees.utils';
import AadhaarNumber from './AadhaarNumber';

const statusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'terminated', label: 'Terminated' },
  { value: 'resigned', label: 'Resigned' },
];
const defaultStatus = 'active';

/** The API's EmploymentStatus enum is capitalised (Active / Resigned / Terminated). */
const toApiStatus = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/** Whole years from a `yyyy-MM-dd` date of birth; null when there is none. */
function ageFrom(dateOfBirth) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const beforeBirthday =
    today.getMonth() < dob.getMonth() ||
    (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate());
  if (beforeBirthday) age -= 1;
  return age >= 0 ? age : null;
}

export default function EmployeeDetailsCard(props) {
  const { employee: e = {} } = props;
  const [status, setStatus] = useState(e.status || defaultStatus);
  const [savingStatus, setSavingStatus] = useState(false);
  const canEdit = useHasMenu('hr.employees', 'EDIT');
  const age = ageFrom(e.dateOfBirth);

  useEffect(() => {
    setStatus(e.status || defaultStatus);
  }, [e.status]);

  const handleStatusChange = async (next: string) => {
    if (!e.id || next === status) return;
    const previous = status;
    setStatus(next);
    setSavingStatus(true);
    try {
      await updateEmploymentStatus(e.id, toApiStatus(next));
      toast.success('Employment status updated');
    } catch (error) {
      setStatus(previous);
      toast.error(
        logError({
          message: 'Failed to update employment status',
          error,
          code: 'EMPLOYMENT_STATUS_UPDATE_FAILED',
        }),
      );
    } finally {
      setSavingStatus(false);
    }
  };

  const dataConfig = [
    {
      rows: [
        { label: 'Employee name', value: e.name },
        {
          type: 'inline',
          items: [
            { label: 'Age', value: age ?? <span className='unavailable-message'>Not Available</span> },
            { label: 'Gender', value: capitalizeString(e.gender) },
          ],
        },
        {
          label: 'Contact number',
          value: e.contact ?? <span className='unavailable-message'>Not Available</span>,
        },
        {
          label: 'Email ID',
          value: e.email ?? <span className='unavailable-message'>Not Available</span>,
        },
        {
          label: 'Designation',
          value: e.designation ?? <span className='unavailable-message'>Not Available</span>,
        },
        {
          label: 'Department',
          value: e.department ?? <span className='unavailable-message'>Not Available</span>,
        },
      ],
    },
    {
      rows: [
        {
          label: 'Aadhaar number',
          // Masked to its last four digits; the row's own button reveals it when needed.
          render: () => <AadhaarNumber value={e.aadhaarNumber} />,
        },
        {
          label: 'PAN Number',
          value: e.panNumber ?? <span className='unavailable-message'>Not Available</span>,
        },
        {
          label: 'Employment period',
          render: () => formatEmploymentPeriod(e.employmentPeriod),
        },
        {
          label: 'Current salary',
          render: () => (
            <div className='row gap-3'>
              <button
                type='button'
                className='btn text-btn text-btn--medium underline'
                onClick={() => toast.info('Coming soon')}
              >
                view details
              </button>{' '}
              {formatAmount(e.currentSalary)}
            </div>
          ),
        },
        {
          label: 'Resume',
          render: () =>
            e.resumeUrl ? (
              <MuiLink
                href={e.resumeUrl}
                target='_blank'
                sx={{ color: 'grey.500', '&:visited': { color: 'grey.500' } }}
              >
                view
              </MuiLink>
            ) : (
              <span className='unavailable-message'>Not Available</span>
            ),
        },
        {
          label: 'Employment Status',
          render: () => (
            <DropdownBtn
              id='employee-status'
              value={status}
              options={statusOptions}
              disabled={!canEdit || savingStatus || !e.id}
              onChange={(event) => handleStatusChange(event.target.value)}
            />
          ),
        },
      ],
    },
  ];

  return <DataGrid columns={dataConfig} />;
}
